// UploadDropzone.jsx (frontend copy)
// Provides drag & drop + click to browse for a single image file.
// Validates type & size; shows preview and remove option.
// TODO: Replace stand-in UI primitives with actual design system components.

import React, { useCallback, useRef, useState } from 'react';

const cn = (...c) => c.filter(Boolean).join(' ');
const Button = ({ className = '', variant = 'outline', disabled, ...rest }) => (
  <button
    {...rest}
    disabled={disabled}
    className={cn(
      'inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
      'border-default/50 bg-surface hover:bg-elevated/60 disabled:opacity-50 disabled:cursor-not-allowed',
      variant === 'primary' && 'bg-[var(--lodia-brand,#2563eb)] text-white hover:opacity-90 border-black/20 shadow-md',
      className
    )}
  />
);

export function UploadDropzone({ value, onChange, disabled, maxFileMB = 10 }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) return 'Unsupported file type';
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > maxFileMB) return `File exceeds ${maxFileMB}MB limit`;
    return '';
  };

  const handleFiles = useCallback((files) => {
    const file = files && files[0];
    if (!file) return;
    const v = validateFile(file);
    if (v) {
      setError(v);
      onChange(null);
    } else {
      setError('');
      onChange(file);
    }
  }, [onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => { e.preventDefault(); if (!disabled) setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };

  return (
    <div className="space-y-2">
      <div
        data-testid="dropzone"
        role="button"
        tabIndex={0}
        aria-label="Image upload dropzone"
        onClick={() => !disabled && inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => { if (e.key === 'Enter') { !disabled && inputRef.current && inputRef.current.click(); } }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'relative flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center outline-none transition',
          dragActive ? 'border-[color:var(--lodia-brand)] bg-[color:var(--lodia-brand)]/5' : 'border-default/60 hover:bg-elevated/40',
          disabled && 'pointer-events-none opacity-50'
        )}
      >
        {!value && (
          <>
            <p className="text-sm text-black">Drag & drop or click to upload image</p>
            <p className="mt-1 text-xs text-black">JPG / PNG / WEBP up to {maxFileMB}MB</p>
          </>
        )}
        {value && (
          <div className="w-full">
            <div className="relative w-full overflow-hidden rounded-xl border bg-black/5" style={{ height: '22rem' }}>
              <img alt="Preview" src={URL.createObjectURL(value)} className="h-full w-full object-contain" />
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="truncate text-xs text-secondary" title={value.name}>{value.name}</span>
              <Button type="button" onClick={(e) => { e.stopPropagation(); onChange(null); }} className="ml-2 text-xs px-2 py-1">Remove</Button>
            </div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled}
        />
      </div>
      {error && <p className="text-xs text-red-500" role="alert">{error}</p>}
    </div>
  );
}

export default UploadDropzone;
